import { useState, useEffect, useReducer } from "react";
import { useForm, useWatch, Controller } from "react-hook-form";
import JobPostingRequestModel from "../../../models/company/jobPosting/request/JobPostingRequestModel";
import { OBJECT_VALUE_RULE, OBJECT_JOB_DESCRIPTION_RULE_REQUIRED, FORM_SELECT_RULE } from "../../../utils/validation-utils";
import IdNameModel from "../../../models/common/IdNameModel";
import toasterUtils from "../../../utils/toaster-utils";
import { GuidEnum } from "../../../models/enums/GuidEnum";
import JobPostingSkillRequestModel from "../../../models/company/jobPosting/request/JobPostingSkillRequestModel";
import masterDataService from "../../../services/master-data-service";
import { PageModeEnum } from "../../../models/enums/PageModeEnum";
import { StatusEnum } from "../../../models/enums/StatusEnum";
import { InterviewTypeEnum } from "../../../models/enums/InterviewTypeEnum";
import { Accordion, AccordionSummary, AccordionDetails, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField, Checkbox, FormControlLabel, Button, FormLabel, RadioGroup, Radio, IconButton, Autocomplete } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

interface JobPostingProps {
    objJobPosting: JobPostingRequestModel;
    actionType: string;
    jobTypes: IdNameModel[];
    assessmentTypes: IdNameModel[];
    domains: IdNameModel[];
    primarySkills: IdNameModel[];
    experiences: IdNameModel[];
    cities: IdNameModel[];
    designations: IdNameModel[];
    salaries: IdNameModel[];
    onCloseJobPosting: () => void;
    onJobPostingSave: (objJobPosting: JobPostingRequestModel) => void;
}

const JobPostingCreateComponent = (props: JobPostingProps) => {
    const [formModel, setFormModel] = useState<JobPostingRequestModel>(props.objJobPosting);
    const [secondarySkillList, setSecondarySkillList] = useState<IdNameModel[]>([]);
    const [secondarySkillListJobPosting, setSecondarySkillListJobPosting] = useState<JobPostingSkillRequestModel[]>(props.objJobPosting.jobPostingSkillRequestModel);
    const [expanded, setExpanded] = useState<false | "job" | "tech" | "general">("job");

    const {
        register,
        setValue,
        control,
        handleSubmit,
        trigger,
        getValues,
        unregister,
        formState: { errors, isValid },
    } = useForm<JobPostingRequestModel>({
        mode: "onChange",
        shouldUnregister: true,
        defaultValues: {
            ...formModel,
            startDate: formModel.startDate ? (dayjs(formModel.startDate) as any) : null,
            endDate: formModel.endDate ? (dayjs(formModel.endDate) as any) : null,
            jobPostingSkillRequestModel: formModel.jobPostingSkillRequestModel,
        },
    });

    const interviewTypeSelected = useWatch({
        name: "interviewTypeId",
        control: control,
        defaultValue: formModel ? formModel.interviewTypeId : InterviewTypeEnum.Video,
    });

    useEffect(() => {
        setFormModel(props.objJobPosting);
        setValue("interviewTypeId", props.objJobPosting.interviewTypeId);
    }, []);

    const getSecondarySkills = (primarySkillId: number) => {
        if (primarySkillId !== 0) {
            //modalUtils.showLoader();
            masterDataService.loadSecondarySkills(primarySkillId).then((res) => {
                //modalUtils.closeModal();
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                setSecondarySkillList(res.items);
            }).catch((error) => {
                //modalUtils.closeModal();
                toasterUtils.showError("Erorr fetching Primary skills");
            });
        } else {
            toasterUtils.showError("Please Select Primary Skill");
        }
    };

    const onSubmit = (formData: JobPostingRequestModel) => {
        if (!isValid) {
            toasterUtils.showError("Please fill all mandatory fields");
            return;
        }
        const skills = (formData.jobPostingSkillRequestModel ?? []).map(s => s.secondarySkillId).filter((x) => x !== null && x !== 0);
        const hasDuplicateSecondarySkill = skills.some((id, i) => skills.indexOf(id) !== i);
        if (hasDuplicateSecondarySkill) {
            toasterUtils.showError("Please select different secondary skills.");
            return;
        }
        if (formData.startDate !== null && formData.endDate !== null) {
            if (formData.startDate > formData.endDate) {
                toasterUtils.showError("End Date should be greater than Start Date are Mandatory");
            } else {
                if (formData.jobPostingSkillRequestModel === undefined) {
                    formData.jobPostingSkillRequestModel = formModel.jobPostingSkillRequestModel;
                }
                if (formData.jobPostingSkillRequestModel && props.actionType === PageModeEnum.Create) {
                    formData.jobPostingSkillRequestModel.forEach(function (item, index) {
                        item.requirementSkillId = GuidEnum.Empty;
                        item.requirementId = GuidEnum.Empty;
                        item.secondarySkillId = Number(item.secondarySkillId);
                        item.secondarySkillNumber = index + 1;
                        item.requirementInterviewProcessId = GuidEnum.Empty;
                        item.recruiterId = GuidEnum.Empty;
                        item.userId = GuidEnum.Empty;
                    });
                }
                if (props.actionType === PageModeEnum.Edit) {
                    formData.requirementId = props.objJobPosting.requirementId;
                }
                formData.userId = GuidEnum.Empty;
                formData.additionalSkills = formData.keyResponsibilities;
                formData.statusId = Number(StatusEnum.DefaultJobPostingStatus);
                props.onJobPostingSave(formData);
            }
        } else {
            toasterUtils.showError("Start Date & End Date are Mandatory");
        }
    };

    const addNewSkill = () => {
        let skill: JobPostingSkillRequestModel = {
            requirementSkillId: GuidEnum.Empty,
            requirementId: GuidEnum.Empty,
            secondarySkillId: null,
            secondarySkillNumber: secondarySkillListJobPosting.length + 1,
            isCommentsRequired: false,
            requirementInterviewProcessId: GuidEnum.Empty,
            recruiterId: GuidEnum.Empty,
            userId: GuidEnum.Empty
        };
        const updatedList = [...secondarySkillListJobPosting]; // simply create a new array and spread the previous one
        updatedList.push(skill);
        setSecondarySkillListJobPosting(updatedList);
        const updatedFormData = { ...formModel };
        updatedFormData.jobPostingSkillRequestModel = updatedList;
        setFormModel(updatedFormData);
    };

    const removeSkill = (index: number) => {
        const updatedList = formModel.jobPostingSkillRequestModel.filter((_, i) => i !== index);
        setSecondarySkillListJobPosting(updatedList);
        const updatedFormData = { ...formModel, jobPostingSkillRequestModel: updatedList };
        setFormModel(updatedFormData);
        unregister(`jobPostingSkillRequestModel.${index}`);
    }

    const interviewTypeClick = (e: any) => {
        setValue("interviewTypeId", e.target.value);
        if (Number(e.target.value) === InterviewTypeEnum.CodeTest) {
            setValue("addInterviewProcess", true);
        } else {
            setValue("addInterviewProcess", false);
        }
    };

    const handleAccordionChange = (panel: "job" | "tech" | "general") => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <form className="contact quform" onSubmit={handleSubmit(onSubmit)}>
            <Accordion expanded={expanded === "job"} onChange={handleAccordionChange("job")}>
                <AccordionSummary className="title-text-accordion" expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Job Info</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <div className="row">
                        <div className="col-md-6">
                            <Controller name="jobTypeId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.jobTypes.find(x => x.id === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.jobTypes} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Job Type" size="small" required margin="normal" fullWidth error={!!errors.jobTypeId} helperText={errors.jobTypeId?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-6">
                            <FormControl fullWidth margin="normal" error={!!errors.jobCode}>
                                <TextField type="text" label="Job Code" size="small" {...register("jobCode", OBJECT_VALUE_RULE)} error={!!errors.jobCode}
                                    helperText={errors.jobCode?.message} autoComplete="off" placeholder="Job Code" disabled={props.actionType === PageModeEnum.Edit} required />
                            </FormControl>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <FormControl fullWidth margin="normal" error={!!errors.jobDescription}>
                                <TextField label="Job Description" size="small" multiline rows={3} {...register("jobDescription", OBJECT_JOB_DESCRIPTION_RULE_REQUIRED)}
                                    error={!!errors.jobDescription} helperText={errors.jobDescription?.message} autoComplete="off" required />
                            </FormControl>
                        </div>
                        <div className="col-md-6">
                            <FormControl fullWidth margin="normal" error={!!errors.keyResponsibilities}>
                                <TextField label="Client Inputs" size="small" multiline rows={3}
                                    {...register("keyResponsibilities", OBJECT_JOB_DESCRIPTION_RULE_REQUIRED)} error={!!errors.keyResponsibilities} helperText={errors.keyResponsibilities?.message} autoComplete="off" required />
                            </FormControl>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <Controller name="interviewTypeId" control={control} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <FormControl fullWidth margin="normal" error={!!errors.interviewTypeId} disabled={props.actionType === PageModeEnum.Edit}>
                                        <FormLabel required>Assessment Type</FormLabel>
                                        <RadioGroup                                            {...field} row={false}
                                            onChange={(e) => {
                                                field.onChange(e);
                                                interviewTypeClick(e);
                                            }}>
                                            {props.assessmentTypes.map((type, index) => (
                                                <FormControlLabel key={index} value={type.id.toString()} control={<Radio />} label={type.name} />
                                            ))}
                                        </RadioGroup>
                                        <FormHelperText>{errors.interviewTypeId?.message}</FormHelperText>
                                    </FormControl>
                                )}
                            />
                            <FormControl fullWidth margin="normal" error={!!errors.assessmentName}>
                                <TextField type="text" label="Assessment Name" size="small" required={Number(interviewTypeSelected) === InterviewTypeEnum.CodeTest}
                                    {...register("assessmentName")} error={!!errors.assessmentName} helperText={errors.assessmentName?.message} autoComplete="off" placeholder="Assessment Name" disabled={Number(interviewTypeSelected) !== InterviewTypeEnum.CodeTest} />
                            </FormControl>
                        </div>
                        <div className="col-md-6">
                            <Controller name="scheduleZoomInterview" control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox {...field} checked={field.value || false} />
                                        }
                                        label="Schedule Zoom Interview"
                                    />
                                )}
                            />
                            <Controller name="addInterviewProcess" control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox {...field} checked={field.value || false}
                                                disabled={
                                                    props.actionType === PageModeEnum.Edit ||
                                                    Number(interviewTypeSelected) === InterviewTypeEnum.CodeTest
                                                }
                                            />
                                        }
                                        label="Add Interview Process Steps"
                                    />
                                )}
                            />
                            <Controller name="isCalibrationCallCompleted" control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox {...field} checked={field.value || false} />
                                        }
                                        label="Calibration Call Completed"
                                    />
                                )}
                            />
                            <Controller name="isClientApproved" control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox {...field} checked={field.value || false} />
                                        }
                                        label="Client Approved"
                                    />
                                )}
                            /><br></br>
                            <Controller name="panelsAvailable" control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox {...field} checked={field.value || false} />
                                        }
                                        label="Panels Available"
                                    />
                                )}
                            />
                        </div>
                    </div>
                </AccordionDetails>
            </Accordion>
            {props.actionType === PageModeEnum.Create &&
                <Accordion expanded={expanded === "tech"} onChange={handleAccordionChange("tech")}>
                    <AccordionSummary className="title-text-accordion" expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Technology</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <div className="row">
                            <div className="col-md-6">
                                <Controller name="domainId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                    render={({ field }) => (
                                        <Autocomplete {...field} value={props.domains.find(x => x.id === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.domains} getOptionLabel={(option) => option.name || ""}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Domain" size="small" required margin="normal" fullWidth error={!!errors.domainId} helperText={errors.domainId?.message} />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    )}
                                />
                            </div>
                            <div className="col-md-6">
                                <Controller name="primarySkillId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                    render={({ field }) => (
                                        <Autocomplete {...field} value={props.primarySkills.find(x => x.id === field.value) || null}
                                            onChange={(_, newValue) => { field.onChange(newValue ? newValue.id : 0); getSecondarySkills(newValue ? newValue.id : 0); }} options={props.primarySkills} getOptionLabel={(option) => option.name || ""}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Primary Skill" size="small" required margin="normal" fullWidth error={!!errors.primarySkillId} helperText={errors.primarySkillId?.message} />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        {formModel.jobPostingSkillRequestModel.map((skill, index) => (
                            <div className="row">
                                <div className="col-md-1">
                                    <IconButton color="error" size="small" onClick={() => removeSkill(index)} disabled={index < 3} className="remove-skill-icon">
                                        <RemoveIcon />
                                    </IconButton>
                                </div>
                                <div className="col-md-7">
                                    <Controller name={`jobPostingSkillRequestModel.${index}.secondarySkillId`} control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                        render={({ field }) => (
                                            <Autocomplete {...field} value={secondarySkillList.find(x => x.id === field.value) || null}
                                                onChange={(_, newValue) => { field.onChange(newValue ? newValue.id : 0) }} options={secondarySkillList} getOptionLabel={(option) => option.name || ""}
                                                renderInput={(params) => (
                                                    <TextField {...params} label={`Secondary Skill ${index + 1}`} size="small" required margin="normal" fullWidth error={!!errors.jobPostingSkillRequestModel?.[index]?.secondarySkillId} helperText={errors.jobPostingSkillRequestModel?.[index]?.secondarySkillId?.message} />
                                                )}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <Controller name={`jobPostingSkillRequestModel.${index}.isCommentsRequired`} control={control}
                                        render={({ field }) => (
                                            <FormControl margin="normal" error={!!errors?.jobPostingSkillRequestModel?.[index]?.isCommentsRequired}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox {...field} checked={field.value || false} disabled={(index + 1) < 4} />
                                                    }
                                                    label="Need Comments"
                                                />
                                                <FormHelperText>
                                                    {errors?.jobPostingSkillRequestModel?.[index]?.isCommentsRequired?.message}
                                                </FormHelperText>
                                            </FormControl>
                                        )}
                                    />
                                </div>
                            </div>
                        ))}
                        <div className="row">
                            <div className="col-md-6"></div>
                            <div className="col-md-6">
                                <Button variant="outlined" color="primary" type="submit" onClick={addNewSkill}>
                                    + Add New Skill
                                </Button>
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            }
            <Accordion expanded={expanded === "general"} onChange={handleAccordionChange("general")}>
                <AccordionSummary className="title-text-accordion" expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" >General Info</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <div className="row">
                        <div className="col-md-6">
                            <Controller name="minExperienceId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.experiences.find(x => x.id === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.experiences} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Min Experience" size="small" required margin="normal" fullWidth error={!!errors.minExperienceId} helperText={errors.minExperienceId?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-6">
                            <Controller name="maxExperienceId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.experiences.find(x => x.id === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.experiences} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Max Experience" size="small" required margin="normal" fullWidth error={!!errors.maxExperienceId} helperText={errors.maxExperienceId?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller name="startDate" control={control} rules={{ required: "Start Date is required" }}
                                    render={({ field, fieldState }) => (
                                        <FormControl fullWidth margin="normal" error={!!fieldState.error}>
                                            <DatePicker
                                                label="Posting Start Date"
                                                value={field.value}
                                                onChange={field.onChange}
                                                format="YYYY-MMM-DD"
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        error: !!fieldState.error,
                                                        helperText: fieldState.error?.message,
                                                        onKeyDown: (e) => e.preventDefault(),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                        <div className="col-md-6">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Controller name="endDate" control={control} rules={{ required: "End Date is required" }}
                                    render={({ field, fieldState }) => (
                                        <FormControl fullWidth margin="normal" error={!!fieldState.error}>
                                            <DatePicker
                                                label="Posting End Date"
                                                value={field.value}
                                                onChange={field.onChange}
                                                format="YYYY-MMM-DD"
                                                slotProps={{
                                                    textField: {
                                                        size: "small",
                                                        fullWidth: true,
                                                        error: !!fieldState.error,
                                                        helperText: fieldState.error?.message,
                                                        onKeyDown: (e) => e.preventDefault(),
                                                    },
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                            </LocalizationProvider>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <Controller name="cityId" control={control} defaultValue={0} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.cities.find(x => x.id === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.id : 0)} options={props.cities} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Current Location" size="small" required margin="normal" fullWidth error={!!errors.cityId} helperText={errors.cityId?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                    />
                                )}
                            />
                        </div>
                        <div className="col-md-6">
                            <Controller name="designationId" control={control} defaultValue={GuidEnum.Empty} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.designations.find(x => x.guId === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.guId : GuidEnum.Empty)} options={props.designations} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Designation" size="small" required margin="normal" fullWidth error={!!errors.designationId} helperText={errors.designationId?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-6">
                            <Controller name="highestPay" control={control} defaultValue={GuidEnum.Empty} rules={FORM_SELECT_RULE}
                                render={({ field }) => (
                                    <Autocomplete {...field} value={props.salaries.find(x => x.guId === field.value) || null}
                                        onChange={(_, newValue) => field.onChange(newValue ? newValue.guId : GuidEnum.Empty)} options={props.salaries} getOptionLabel={(option) => option.name || ""}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Highest Pay" size="small" required margin="normal" fullWidth error={!!errors.highestPay} helperText={errors.highestPay?.message} />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </AccordionDetails>
            </Accordion>
            <div className="row">
                <div className="col-md-12 text-end mt-2">
                    <Button variant="contained" color="success" type="submit" disabled={!isValid} sx={{ mt: 2 }}>
                        Save
                    </Button>
                    <Button variant="contained" color="error" className="m-l-xxs" type="button" onClick={props.onCloseJobPosting} sx={{ mt: 2 }}>
                        Close
                    </Button>
                </div>
            </div>
        </form>
    );
};
export default JobPostingCreateComponent;